
// Levenshtein distance for arrays (generic)
export const calculateLevenshtein = <T>(a: T[], b: T[]): number => {
    const matrix = Array.from({ length: a.length + 1 }, () =>
        Array(b.length + 1).fill(0)
    );

    for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= b.length; j++) matrix[0][j] = j;

    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1,     // deletion
                matrix[i][j - 1] + 1,     // insertion
                matrix[i - 1][j - 1] + cost // substitution
            );
        }
    }
    return matrix[a.length][b.length];
};

export const normalizeText = (s: string) => s.toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "").trim();

export const calculateWER = (reference: string, hypothesis: string): number => {
    const refWords = normalizeText(reference).split(/\s+/).filter(w => w.length > 0);
    const hypWords = normalizeText(hypothesis).split(/\s+/).filter(w => w.length > 0);

    if (refWords.length === 0) return hypWords.length > 0 ? 100 : 0;

    const distance = calculateLevenshtein(refWords, hypWords);
    return (distance / refWords.length) * 100;
};

export const calculateCER = (reference: string, hypothesis: string): number => {
    // For CER, we typically keep case sensitivity or not? Usually ASR evaluation normalizes case.
    // We will normalize case but keep punctuation if needed, or remove it.
    // Let's stick to simple normalization: lowercase.
    const refChars = reference.toLowerCase().split('');
    const hypChars = hypothesis.toLowerCase().split('');

    if (refChars.length === 0) return hypChars.length > 0 ? 100 : 0;

    const distance = calculateLevenshtein(refChars, hypChars);
    return (distance / refChars.length) * 100;
};

export type DiffType = 'match' | 'substitution' | 'insertion' | 'deletion';

export interface DiffSegment {
    type: DiffType;
    original?: string;
    generated?: string;
}

export const calculateDiff = (reference: string, hypothesis: string): DiffSegment[] => {
    const refWords = normalizeText(reference).split(/\s+/).filter(w => w.length > 0);
    const hypWords = normalizeText(hypothesis).split(/\s+/).filter(w => w.length > 0);

    const dp: number[][] = Array.from({ length: refWords.length + 1 }, () =>
        Array(hypWords.length + 1).fill(0)
    );

    // Initialize
    for (let i = 0; i <= refWords.length; i++) dp[i][0] = i;
    for (let j = 0; j <= hypWords.length; j++) dp[0][j] = j;

    // Fill DP table
    for (let i = 1; i <= refWords.length; i++) {
        for (let j = 1; j <= hypWords.length; j++) {
            if (refWords[i - 1] === hypWords[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1];
            } else {
                dp[i][j] = Math.min(
                    dp[i - 1][j] + 1,     // Deletion
                    dp[i][j - 1] + 1,     // Insertion
                    dp[i - 1][j - 1] + 1  // Substitution
                );
            }
        }
    }

    // Backtrack
    const diff: DiffSegment[] = [];
    let i = refWords.length;
    let j = hypWords.length;

    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && refWords[i - 1] === hypWords[j - 1]) {
            diff.unshift({ type: 'match', original: refWords[i - 1], generated: hypWords[j - 1] });
            i--;
            j--;
        } else if (j > 0 && (i === 0 || dp[i][j] === dp[i][j - 1] + 1)) {
            diff.unshift({ type: 'insertion', generated: hypWords[j - 1] });
            j--;
        } else if (i > 0 && (j === 0 || dp[i][j] === dp[i - 1][j] + 1)) {
            diff.unshift({ type: 'deletion', original: refWords[i - 1] });
            i--;
        } else {
            diff.unshift({ type: 'substitution', original: refWords[i - 1], generated: hypWords[j - 1] });
            i--;
            j--;
        }
    }

    return diff;
};
